import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ReactNode,
  useMemo,
  forwardRef
} from 'react';
import LoadingIndicator from 'components/common/LoadingIndicator';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'link'
  | 'icon';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface BaseButtonProps extends Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  'children'
> {
  title?: string;
  icon?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  additionalStyles?: string;
  children?: ReactNode;
  disableDarkMode?: boolean;
  isLoading?: boolean;
  loadingStyles?: string;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'text-white bg-primary-700 hover:bg-primary-800 active:text-white active:bg-primary-800 disabled:bg-primary-100 disabled:text-gray-500/50 disabled:hover:bg-primary-100 disabled:hover:text-gray-500/50 disabled:cursor-not-allowed shadow-sm',
  secondary:
    'text-primary-900 ring-1 ring-primary-500 ring-inset bg-primary-100 hover:ring-primary-200 active:text-primary-800 active:ring-primary-400 disabled:text-gray-500/50 disabled:hover:bg-primary-100 disabled:hover:ring-primary-200 disabled:cursor-not-allowed disabled:ring-primary-200 shadow-sm',
  tertiary:
    'text-primary-900 bg-primary-50 active:text-primary-900 disabled:text-gray-500/50 disabled:bg-white disabled:hover:bg-white disabled:hover:text-gray-500/50 disabled:cursor-not-allowed shadow-sm',
  link: 'text-primary-900 bg-transparent hover:text-primary-800 hover:underline underline-offset-2 disabled:text-gray-500/50 disabled:hover:text-gray-500/50 disabled:hover:no-underline disabled:cursor-not-allowed shadow-none',
  icon: 'text-primary-900 bg-transparent disabled:text-gray-500/50 disabled:hover:text-gray-500/50 disabled:cursor-not-allowed shadow-none'
};

const darkModeVariantStyles: Record<ButtonVariant, string> = {
  primary:
    'dark:text-secondary-dark-900 dark:bg-secondary-dark-500 dark:hover:bg-secondary-dark-600 dark:active:bg-secondary-dark-600 dark:disabled:bg-secondary-dark-200 dark:disabled:text-secondary-dark-600 dark:disabled:hover:bg-secondary-dark-200 dark:ring-0',
  secondary:
    'dark:text-secondary-dark-900 dark:bg-secondary-dark-400 dark:ring-1 dark:ring-secondary-dark-500 dark:hover:bg-secondary-dark-500 dark:active:bg-secondary-dark-500 dark:disabled:bg-secondary-dark-200 dark:disabled:text-secondary-dark-600 dark:disabled:hover:bg-secondary-dark-200 dark:disabled:ring-secondary-dark-400 dark:ring-inset',
  tertiary:
    'dark:text-secondary-dark-900 dark:bg-secondary-dark-400 dark:hover:bg-secondary-dark-500 dark:active:bg-secondary-dark-500 dark:disabled:bg-secondary-dark-200 dark:disabled:hover:bg-secondary-dark-200 dark:disabled:text-secondary-dark-600 dark:ring-0',
  link: 'dark:text-secondary-dark-700 dark:hover:text-secondary-dark-900 dark:hover:underline underline-offset-2 dark:bg-transparent dark:disabled:hover:text-secondary-dark-600 dark:disabled:hover:no-underline',
  icon: 'dark:text-secondary-dark-700 dark:hover:text-secondary-dark-900 dark:disabled:hover:text-secondary-dark-600 dark:bg-transparent dark:shadow-none'
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: '~text-[0.625rem]/xs ~px-1/2 ~py-0.5/1',
  sm: '~text-xs/sm ~px-1.5/3 ~py-1/1.5',
  md: '~text-xs/base ~px-2/4 ~py-1.5/2',
  lg: '~text-sm/lg ~px-2.5/5 ~py-2/2.5',
  xl: '~text-xl/[1.75rem] ~px-6/10 ~py-3/4 font-semibold'
};

const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      title,
      icon,
      variant = 'primary',
      size = 'md',
      additionalStyles,
      children,
      disableDarkMode = false,
      isLoading = false,
      loadingStyles,
      type = 'button',
      className,
      disabled = false,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;
    const baseClasses =
      'h-fit rounded-md shadow-sm dark:shadow-none flex items-center justify-center ~gap-1/1.5 hover-scale';

    const variantClass = variantStyles[variant];
    const sizeClass = sizeStyles[size];

    const darkModeClass = useMemo(() => {
      if (disableDarkMode) return '';

      if (variant === 'link' || variant === 'icon') {
        return darkModeVariantStyles[variant];
      }

      return darkModeVariantStyles[variant];
    }, [disableDarkMode, variant]);

    const buttonClassName = useMemo(() => {
      const classes = [
        baseClasses,
        sizeClass,
        variantClass,
        darkModeClass,
        additionalStyles,
        className
      ].filter(Boolean);
      return classes.join(' ');
    }, [
      baseClasses,
      sizeClass,
      variantClass,
      darkModeClass,
      additionalStyles,
      className
    ]);

    const content = useMemo(() => {
      if (isLoading) {
        return <LoadingIndicator styles={loadingStyles} />;
      }

      return (
        <>
          {icon && icon}
          {title && <span>{title}</span>}
        </>
      );
    }, [isLoading, loadingStyles, icon, title]);

    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={buttonClassName}>
        {content}
        {children}
      </button>
    );
  }
);

BaseButton.displayName = 'BaseButton';

export const BaseButtonPrimary = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'variant'>
>((props, ref) => <BaseButton {...props} ref={ref} variant='primary' />);
BaseButtonPrimary.displayName = 'BaseButtonPrimary';

export const BaseButtonSecondary = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'variant'>
>((props, ref) => <BaseButton {...props} ref={ref} variant='secondary' />);
BaseButtonSecondary.displayName = 'BaseButtonSecondary';

export const BaseButtonTertiary = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'variant'>
>((props, ref) => <BaseButton {...props} ref={ref} variant='tertiary' />);
BaseButtonTertiary.displayName = 'BaseButtonTertiary';

export const BaseButtonLink = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'variant'>
>((props, ref) => <BaseButton {...props} ref={ref} variant='link' />);
BaseButtonLink.displayName = 'BaseButtonLink';

export const BaseButtonIcon = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'variant'>
>((props, ref) => <BaseButton {...props} ref={ref} variant='icon' />);
BaseButtonIcon.displayName = 'BaseButtonIcon';

export const BaseButtonXs = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'size'>
>((props, ref) => <BaseButton {...props} ref={ref} size='xs' />);
BaseButtonXs.displayName = 'BaseButtonXs';

export const BaseButtonSm = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'size'>
>((props, ref) => <BaseButton {...props} ref={ref} size='sm' />);
BaseButtonSm.displayName = 'BaseButtonSm';

export const BaseButtonMd = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'size'>
>((props, ref) => <BaseButton {...props} ref={ref} size='md' />);
BaseButtonMd.displayName = 'BaseButtonMd';

export const BaseButtonLg = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'size'>
>((props, ref) => <BaseButton {...props} ref={ref} size='lg' />);
BaseButtonLg.displayName = 'BaseButtonLg';

export const BaseButtonXl = forwardRef<
  HTMLButtonElement,
  Omit<BaseButtonProps, 'size'>
>((props, ref) => <BaseButton {...props} ref={ref} size='xl' />);
BaseButtonXl.displayName = 'BaseButtonXl';

export default BaseButton;
